import { HttpClient } from "@utils/httpClient";
import type { EncryptionKeyStatus, Rotation, RotationStart } from "@models/encryptionKey";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;
const PREFIX = "/admin/providers/encryption-key";

/** The platform encryption key (`/xplatform/{env}/encryption-key`) and its rotations, in core. */
class EncryptionKeyApi {
  private readonly http: HttpClient;

  constructor() {
    if (!BASE_URL) throw new Error("VITE_XPLATFORM_CORE_API_BASE_URL is not configured");
    this.http = new HttpClient(BASE_URL);
  }

  status(signal?: AbortSignal): Promise<EncryptionKeyStatus> {
    return this.http.makeAuthenticatedRequest<EncryptionKeyStatus>(`${PREFIX}/status`, { method: "GET", signal });
  }

  /** 202 `{id}`; 409 when a rotation runs, 412 without the prerequisites confirmed. */
  start(body: RotationStart): Promise<{ id: string }> {
    return this.http.makeAuthenticatedRequest<{ id: string }>(`${PREFIX}/rotations`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  rotation(rotationId: string, signal?: AbortSignal): Promise<Rotation> {
    return this.http.makeAuthenticatedRequest<Rotation>(`${PREFIX}/rotations/${encodeURIComponent(rotationId)}`, {
      method: "GET",
      signal,
    });
  }

  resume(rotationId: string): Promise<Rotation> {
    return this.http.makeAuthenticatedRequest<Rotation>(
      `${PREFIX}/rotations/${encodeURIComponent(rotationId)}/resume`, { method: "POST" });
  }

  /** Only before the retire phase: the old key becomes primary again. */
  rollback(rotationId: string): Promise<Rotation> {
    return this.http.makeAuthenticatedRequest<Rotation>(
      `${PREFIX}/rotations/${encodeURIComponent(rotationId)}/rollback`, { method: "POST" });
  }
}

let instance: EncryptionKeyApi | null = null;
export const encryptionKeyApi = (): EncryptionKeyApi => {
  instance ??= new EncryptionKeyApi();
  return instance;
};
